import { Form, Select } from "antd";
import { Button, Spin } from "../../../BP2Components";
import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { useIntl } from "react-intl";
import IntlMessages from "../../../../util/IntlMessages";
import AddComments from "../../../Comments/AddComments";
import "./MessageModal.less";
import { getGroupMembers } from "../../../../services/restAPI/RestApi";
import { LinkToCase } from "../../CasesList/SingleCase/LinkToCase/LinkToCase";
import clsx from "clsx";

const { Option } = Select;

const sortUsers = (users) =>
   [...users]
      .sort((a, b) => {
         const nameA = `${a.lastName} ${a.firstName}`.trim();
         const nameB = `${b.lastName} ${b.firstName}`.trim();
         return nameA.localeCompare(nameB);
      })
      .filter((user) => user.lastName || user.firstName);

let MessageModal = ({
   action,
   handleNormalAction,
   sysAction,
   handleSysAction,
   closeModal,
   form,
   instanceId,
   recordId,
   className,
   linkToCase,
}) => {
   const intl = useIntl();
   const { getFieldDecorator, setFieldsValue } = form;

   const { listOfAllUsers, allUsersLoading } = useSelector(({ messages }) => ({
      listOfAllUsers: sortUsers(messages.listOfAllUsers || []),
      allUsersLoading: messages.allUsersLoading ?? false,
   }));

   const isSelectRequired = !!sysAction || action?.userChoiceRequired === "1";

   const [selectItems, setSelectItems] = useState([]);
   const [defaultRecipient, setDefaultRecipient] = useState("");
   const [instanceMessage, setInstanceMessage] = useState("");
   const [commentValue, setCommentValue] = useState("");
   const [stepId, setStepId] = useState();
   const [contentLoading, setContentLoading] = useState(false);

   const convertToCorrectMessage = (message) =>
      message?.replace("<b>", "").replace("</b>", "") ?? "";
   const convertToCorrectName = (name) =>
      name?.replace("<b>", "").replace("</b>", "").split(" ").slice(0, 2).join(" ") ?? "";

   useEffect(() => {
      if (!action && !sysAction) return;

      let newSelectItems = [];
      let newDefaultRecipient = "";

      if (action?.recipients) {
         let areUsersSet = false;

         Object.keys(action.recipients).forEach((recipientKey) => {
            const recipients = action.recipients[recipientKey];
            if (!recipients || (!recipients.length && !Object.keys(recipients).length)) return;

            if (recipientKey === "other" && recipients.length === 1) {
               setStepId(recipients[0]?.stepId);
               setInstanceMessage(recipients[0]?.label);
               return;
            }

            if (recipientKey === "users") {
               newSelectItems = recipients.map((recipient) => ({
                  value: recipient.userId,
                  label: convertToCorrectName(recipient.label),
               }));
               if (recipients.length === 1) newDefaultRecipient = recipients[0].userId;
               areUsersSet = true;
            }

            if (recipientKey === "groups") {
               const users = recipients?.users;
               if (users?.length) {
                  newSelectItems = users.map((user) => ({ value: user.id, label: user.name }));
                  setStepId(recipients[0]?.stepId);
                  areUsersSet = true;
               }
            }

            if (recipientKey === "subprocesses") {
               Object.entries(recipients).forEach(
                  ([subProcessRecipientKey, subProcessRecipientElements]) => {
                     if (subProcessRecipientKey === "configuration_groups") {
                        setContentLoading(true);
                        getGroupMembers(subProcessRecipientElements?.[0]?.groupId)
                           .then((res) => {
                              setSelectItems(
                                 res.data.map((user) => ({
                                    value: user.oldId,
                                    label: `${user.lastName} ${user.firstName}`,
                                 })),
                              );
                              setContentLoading(false);
                           })
                           .catch(() => {
                              setContentLoading(false);
                              console.log("Could not find users with given id group");
                           });
                     }
                  },
               );
            }
         });

         if (action.userChoiceRequired === "1" && !areUsersSet && listOfAllUsers.length) {
            newSelectItems = listOfAllUsers.map((user) => ({
               value: user.oldId,
               label: `${user.lastName} ${user.firstName}`,
            }));
         }
      } else if (sysAction && listOfAllUsers.length) {
         if (sysAction.data?.body?.recipientId) {
            const user = listOfAllUsers.find(
               (user) => user.oldId === Number(sysAction.data.body.recipientId),
            );
            if (user) {
               newSelectItems = [
                  { value: user.oldId, label: `${user.lastName} ${user.firstName}` },
               ];
               newDefaultRecipient = user.oldId;
            }
         } else {
            newSelectItems = listOfAllUsers.map((user) => ({
               value: user.oldId,
               label: `${user.lastName} ${user.firstName}`,
            }));
         }
      }

      const isDifferent =
         newSelectItems.length !== selectItems.length ||
         newSelectItems.some((item, idx) => item.value !== selectItems[idx]?.value);

      if (isDifferent) {
         setSelectItems(newSelectItems);
         if (newDefaultRecipient) setDefaultRecipient(newDefaultRecipient);
      }
   }, [action, sysAction, listOfAllUsers]);

   useEffect(() => {
      if (defaultRecipient) {
         setTimeout(() => {
            setFieldsValue({ selectRecipient: defaultRecipient });
         }, 100);
      }
   }, [defaultRecipient]);

   useEffect(() => {
      setTimeout(() => {
         const value = document.querySelector(".comment-textarea.comments-mode")?.innerHTML ?? "";
         const modalElement = document.querySelector(".comment-textarea.modal-mode");
         if (modalElement) modalElement.innerHTML = value;
         setFieldsValue({ comment: value });
         setCommentValue(value);
      }, 100);
   }, []);

   const handleSubmitClick = () => {
      setFieldsValue({ comment: commentValue });
   };

   return (
      <Form
         layout="vertical"
         className={`${className} message-modal-wrapper`}
         onSubmit={(e) => {
            e.preventDefault();
            if (contentLoading) return;
            form.validateFields((err) => {
               if (err) return;
               const handler = action
                  ? handleNormalAction(action, form, stepId, null, closeModal)
                  : handleSysAction(sysAction, form, closeModal);
               handler(e);
            });
         }}
      >
         {contentLoading || allUsersLoading ? (
            <Spin />
         ) : selectItems && selectItems.length && !instanceMessage ? (
            <Form.Item label={<IntlMessages id="modal.recipientInNextStep" />}>
               {getFieldDecorator("selectRecipient", {
                  rules: [
                     {
                        required: isSelectRequired,
                        message: intl.formatMessage({ id: "form.selectRecipient" }),
                     },
                  ],
               })(
                  <Select
                     defaultActiveFirstOption
                     showSearch
                     placeholder={<IntlMessages id="modal.selectRecipient" />}
                     className="select-recipent"
                     filterOption={(input, option) => {
                        const label = option?.props?.children ?? "";
                        const searchTerms = input.toLowerCase().trim().split(/\s+/);
                        const labelText = label.toString().toLowerCase();

                        return searchTerms.every((term) => labelText.includes(term));
                     }}
                     optionFilterProp="children"
                  >
                     {selectItems.map((item) => (
                        <Option key={item.value} value={item.value}>
                           {item.label}
                        </Option>
                     ))}
                  </Select>,
               )}
            </Form.Item>
         ) : !!instanceMessage ? (
            <p>{convertToCorrectMessage(instanceMessage)}</p>
         ) : (
            <IntlMessages id="modal.noData" />
         )}

         <Form.Item label={<IntlMessages id="modal.comment" />}>
            {getFieldDecorator("comment")(
               <AddComments
                  modalMode={true}
                  setCommentValue={setCommentValue}
                  instanceId={instanceId}
                  recordId={recordId}
                  setContentLoading={setContentLoading}
               />,
            )}
         </Form.Item>

         <div className="message-modal__actions-wrapper">
            <Button
               buttonType={contentLoading ? "disabled" : "primary"}
               disabled={contentLoading}
               htmlType="submit"
               onClick={handleSubmitClick}
               className={clsx(
                  "message-modal__submit-button",
                  !linkToCase && "message-modal__submit-button--no-link",
               )}
            >
               <IntlMessages id={action ? action.label : `action.${sysAction.name}`} />
            </Button>
            {linkToCase && <LinkToCase linkToCase={linkToCase} />}
         </div>
      </Form>
   );
};

MessageModal = Form.create({ name: "modal_form" })(MessageModal);

export default MessageModal;
